import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { FloppyDisk, ArrowsClockwise, Fire, Copy } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TuningCard({ icon: Icon, title, description, value, min, max, step, format, preview, current, onChange, testId }) {
  const changed = value !== current;
  return (
    <div className="bg-white border border-gray-200 rounded-md p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} className="text-[#002FA7]" />
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {changed && <span className="ml-auto text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">unsaved</span>}
      </div>
      <p className="text-xs text-gray-500 mb-5">{description}</p>
      <div className="flex items-center gap-4">
        <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={`${testId}-slider`} />
        <span className="w-20 text-right font-mono text-sm font-bold text-gray-900 whitespace-nowrap" data-testid={`${testId}-value`}>{format(value)}</span>
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1"><span>Sensitive · {format(min)}</span><span>Current: {format(current)}</span><span>Strict · {format(max)}</span></div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded border border-gray-200 p-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Pages flagged</p>
          <p className="text-2xl font-black text-gray-900" data-testid={`${testId}-pages`}>{preview?.flagged_pages ?? "—"}</p>
        </div>
        <div className="rounded border border-gray-200 p-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Claims flagged</p>
          <p className="text-2xl font-black text-gray-900" data-testid={`${testId}-claims`}>{preview?.flagged_claims ?? "—"}</p>
        </div>
        <div className="rounded border border-gray-200 p-2.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-gray-400">Max observed</p>
          <p className="text-2xl font-black text-gray-500" data-testid={`${testId}-max`}>{preview ? format(preview.max_observed) : "—"}</p>
        </div>
      </div>
    </div>
  );
}

export default function FraudTuningPanel() {
  const [ela, setEla] = useState(8);
  const [cm, setCm] = useState(12);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounce = useRef(null);

  const fetchPreview = useCallback(async (e, c) => {
    try {
      const r = await axios.get(`${API}/fraud/tuning/preview`, { params: { ela_z: e, cm_min_matches: c } });
      setPreview(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => {
    fetchPreview(8, 12).then(d => { if (d) { setEla(d.ela.current); setCm(d.copy_move.current); fetchPreview(d.ela.current, d.copy_move.current); } });
  }, [fetchPreview]);

  const onChange = (e, c) => {
    setEla(e); setCm(c);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPreview(e, c), 250);
  };

  const save = async (rescan = false) => {
    setSaving(true);
    try {
      await axios.put(`${API}/fraud/tuning`, { ela_z: ela, cm_min_matches: cm });
      toast.success("Thresholds saved — applied on the next analysis");
      if (rescan) {
        const r = await axios.post(`${API}/fraud/batch`, {});
        toast.info(`Re-scanning ${r.data.queued} processed claims with the new thresholds`);
      }
      fetchPreview(ela, cm);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const dirty = preview && (ela !== preview.ela.current || cm !== preview.copy_move.current);

  return (
    <div className="space-y-4" data-testid="fraud-tuning-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Preview is computed live against <span className="font-semibold text-gray-700">{preview?.total_pages ?? "—"} analyzed pages</span> across <span className="font-semibold text-gray-700">{preview?.total_claims ?? "—"} claims</span>. Lower = more sensitive (more flags).
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={saving || !dirty} onClick={() => save(false)} className="gap-1.5 text-xs" data-testid="tuning-save-btn"><FloppyDisk size={14} /> Save</Button>
          <Button size="sm" disabled={saving || !dirty} onClick={() => save(true)} className="gap-1.5 text-xs bg-[#002FA7] text-white hover:bg-blue-800" data-testid="tuning-save-rescan-btn"><ArrowsClockwise size={14} /> Save & re-scan all</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TuningCard icon={Fire} title="ELA block anomaly (IMG-02)" testId="tuning-ela"
                    description="MAD z-score a 32px block's compression error must exceed (vs. the page median) to count as anomalous. Reference default 8; lower catches subtler re-saves, higher reduces noise on scans."
                    value={ela} min={3} max={20} step={0.5} format={v => `z > ${Number(v).toFixed(1)}`} preview={preview?.ela} current={preview?.ela.current ?? ela}
                    onChange={v => onChange(v, cm)} />
        <TuningCard icon={Copy} title="Copy-move clone (IMG-03)" testId="tuning-cm"
                    description="Minimum ORB feature matches sharing one displacement vector before a cloned region is reported. Reference default 12; repeated table cells and logos can legitimately produce 8–15 matches."
                    value={cm} min={3} max={40} step={1} format={v => `${v} matches`} preview={preview?.copy_move} current={preview?.copy_move.current ?? cm}
                    onChange={v => onChange(ela, v)} />
      </div>
    </div>
  );
}
